import { CoreApi, Product, ProductPartial, ProductType } from '@inter-ikea-storageone/range';
import { ComponentIds, Entity, HierarchyUtils, Size } from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import {
  framePlacementOkForAssembly,
  framePlacementOkForInterior
} from '../rules/interiorPlacementRules';
import { AgeGroupComponent } from '@inter-ikea-storageone/range-smastad';

/**
 * Matches front layout assemblies to frame of selected door
 */
export const matchFrontLayoutToFrame = (
  coreApi: CoreApi,
  selectedProduct: Product,
  selectedEntity?: Entity
) => (product: Product) => {
  if (!selectedEntity) return false;
  const frame = HierarchyUtils.findWithComponentInUpwardsHierarchy(
    selectedEntity,
    ComponentIds.FRAME_COMPONENT_ID
  );
  if (!frame || coreApi.entities.isProductType(frame, ProductType.SMASTAD_LOFT_BED)) return false;
  const frameSize = frame.getComponent<CommunicationSizeComponent>(
    ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
  )?.size;

  if (coreApi.entities.isProductType(frame, ProductType.SMASTAD_CHANGING_TABLE))
    return product.coreProductId.includes('changingTable');
  else if (coreApi.entities.isProductType(frame, ProductType.SMASTAD_DESK))
    return product.coreProductId.includes('desk');

  return (
    product.communicatedDimensions.width === frameSize?.width &&
    product.communicatedDimensions.height === frameSize?.height &&
    product.communicatedDimensions.depth === frameSize?.depth
  );
};

export const matchInteriorSize = (coreApi: CoreApi, selectedProduct: Product | ProductPartial) => (
  product: Product | ProductPartial
) => {
  if (product.type === ProductType.SMASTAD_RAIL_STRIP_W_HOOKS_ASM) {
    return selectedProduct.communicatedDimensions.depth === product.communicatedDimensions.depth;
  } else if (selectedProduct.type === ProductType.SMASTAD_PULL_OUT_FRAME_108_ASM) {
    return (
      product.communicatedDimensions.depth === 550 && product.communicatedDimensions.width === 300
    );
  } else if (
    [ProductType.SMASTAD_CHANGING_TABLE, ProductType.SMASTAD_DESK].includes(selectedProduct.type)
  ) {
    return (
      product.communicatedDimensions.depth === 550 &&
      product.type !== ProductType.SMASTAD_CLOTH_RAIL_ASM &&
      product.type !== ProductType.SMASTAD_CLOTH_RAIL_55_W_HOOKS_ASM
    );
  }
  return (
    product.communicatedDimensions.depth === selectedProduct.communicatedDimensions.depth &&
    product.communicatedDimensions.width === selectedProduct.communicatedDimensions.width
  );
};

export const matchOranizersToInteriors = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial
) => (product: Product | ProductPartial) => {
  return (
    (product.dimensions.width <= selectedProduct.dimensions.width &&
      product.dimensions.depth <= selectedProduct.dimensions.depth) ||
    (product.dimensions.width <= selectedProduct.dimensions.depth &&
      product.dimensions.depth <= selectedProduct.dimensions.width)
  );
};

/**
 * Don't show wire baskets for wall hanging frame or frame stacked above 120cm
 */
export const matchInteriorsToFramePlacement = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial,
  selectedEntity?: Entity
) => (product: Product | ProductPartial) => {
  if (!selectedEntity) return false;
  return framePlacementOkForInterior(selectedEntity, product, coreApi);
};

/**
 * Only show bench drawers if selected entity is a bench
 */
export const matchDrawerToBench = (
  coreApi: CoreApi,
  selectedProduct: Product,
  selectedEntity?: Entity
) => () => {
  if (!selectedEntity || !coreApi.entities.isProductType(selectedEntity, ProductType.SMASTAD_BENCH))
    return false;
  return true;
};

/**
 * Only show product for current ageGroup
 */
export const matchAgeGroup = (coreApi: CoreApi) => (product: Product) => {
  const currentAgeGroup =
    coreApi.entities.getScene()?.getComponent<AgeGroupComponent>(AgeGroupComponent.id)?.ageGroup ||
    '3-7';
  return product.meta?.ageGroup === currentAgeGroup;
};

/**
 * Only display boxes less than 260cm width for desk but all for changing table
 */
export const matchOranizersToChangingTableDesk = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial
) => (product: Product | ProductPartial) => {
  return selectedProduct.type === ProductType.SMASTAD_CHANGING_TABLE
    ? true
    : product.dimensions.width < 260;
};

/**
 * Check if assembly is OK for frame with regards to interior height rules
 */
export const matchInteriorHeightRules = (
  coreApi: CoreApi,
  selectedProduct: Product,
  selectedEntity?: Entity
) => (product: Product) => {
  if (selectedEntity) {
    return framePlacementOkForAssembly(selectedEntity, product, coreApi);
  }
  return true;
};

export const matchFrontLayoutToLoftBedDesk = () => (product: Product) => {
  const hasDoors = product.parts.some(
    part => part.product?.type === ProductType.SMASTAD_DOOR_FRONT
  );
  return (
    !hasDoors &&
    product.communicatedDimensions.width === 600 &&
    product.communicatedDimensions.depth === 550 &&
    product.communicatedDimensions.height === 600
  );
};

export const matchInteriorSuggestionToLoftBedCabinet = () => (product: Product) => {
  const hasDrawers = product.parts.some(
    part => part.product?.type === ProductType.SMASTAD_DRAWER_FRONT
  );
  return (
    !hasDrawers &&
    product.communicatedDimensions.width === 600 &&
    product.communicatedDimensions.depth === 550 &&
    product.communicatedDimensions.height === 1200
  );
};

export const matchFrameFrontSize = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial,
  selectedEntity?: Entity
) => (product: Product | ProductPartial) => {
  if (!selectedEntity || !selectedEntity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) return true;
  const frontsOnFrame = coreApi.entities
    .getFrameDescendants(selectedEntity)
    .filter(
      child =>
        (child.hasComponent(ComponentIds.DOOR_COMPONENT_ID) ||
          child.hasComponent(ComponentIds.DRAWER_COMPONENT_ID)) &&
        !child.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID)
    );
  if (frontsOnFrame.length > 0) {
    const frontSizesOnFrame = frontsOnFrame
      .map(
        front =>
          front.getComponent<CommunicationSizeComponent>(
            ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
          )?.size
      )
      .filter(Boolean) as Size[];

    return !!frontSizesOnFrame.find(
      front =>
        front.width === product.communicatedDimensions.width &&
        front.height === product.communicatedDimensions.height
    );
  } else if (selectedProduct.type === ProductType.SMASTAD_LOFT_BED) {
    return (
      product.type === ProductType.SMASTAD_DRAWER_ASM ||
      product.communicatedDimensions.height === 1200
    );
  }
  return true;
};
